import React, { useEffect, useState, useRef } from 'react';
import { View, Animated } from 'react-native';

import { LightThemeColors, withTheme } from '@vfgroup-oneplatform/foundation/Components/Themes';
import { usePrevious } from '@vfgroup-oneplatform/foundation/Utils/Hooks';
import { ViewPropTypes, TextPropTypes } from 'deprecated-react-native-prop-types';
import {
  isUndefined as _isUndefined,
  isFunction as _isFunction,
  map as _map,
  cloneDeep as _cloneDeep,
  reverse as _reverse,
  slice as _slice,
  find as _find,
} from 'lodash';
import memoize from 'memoize-one';
import moment from 'moment';
import PropTypes from 'prop-types';

import { DateFormatters } from '../../../Utils';

import styles from './BarChart.Styles';
import { Bars, VerticalLabels } from './Components';

const ANIMATION_DURATION = 300;



const timingAnimation = (
  animatedValue,
  toValue,
  duration,
  delay,
  { onBarAnimationEnd, currentActiveIndex, previousActiveIndex, data, barsData } = {},
) => {
  Animated.timing(animatedValue, {
    toValue,
    duration,
    delay,
    useNativeDriver: false,
  }).start(({ finished }) => {
    if (_isFunction(onBarAnimationEnd)) {
      onBarAnimationEnd({
        finished,
        currentActiveIndex,
        previousActiveIndex,
        data,
        barsData,
      });
    }
  });
};

const getBillAnimatedStyle = (
  animatedValue,
  isYearVisible,
  index,
  currentActiveIndex,
  previousActiveIndex,
) => {
  const marginValue = isYearVisible ? 122 : 95;

  const isOpeningAnimation =
    index === currentActiveIndex ||
    (index === previousActiveIndex && _isUndefined(currentActiveIndex));
  const isClosingAnimation = index === previousActiveIndex && !_isUndefined(currentActiveIndex);
  const marginRightAnimatedStyle = {};
  const opacityAnimatedStyle = {};
  if (isOpeningAnimation) {
    marginRightAnimatedStyle.marginRight = animatedValue.interpolate({
      inputRange: [0, 1],
      outputRange: [0, marginValue],
    });
    opacityAnimatedStyle.opacity = animatedValue.interpolate({
      inputRange: [0, 0.5, 1],
      outputRange: [0, 0, 1],
    });
  } else {
    marginRightAnimatedStyle.marginRight = isClosingAnimation
      ? animatedValue.interpolate({
        inputRange: [0, 1],
        outputRange: [marginValue, 0],
      })
      : 4;

    opacityAnimatedStyle.opacity = isClosingAnimation
      ? animatedValue.interpolate({
        inputRange: [0, 0.65, 1],
        outputRange: [1, 0, 0],
      })
      : 0;
  }
  return { marginRightAnimatedStyle, opacityAnimatedStyle };
};

function getFormattingConfig(
  isStartDateBillDate,
  getTimePeriod,
  getYear,
  getMonth,
  getShortMonth,
  getMonthNumber,
) {
  const _getTimePeriod = _isFunction(getTimePeriod) ? getTimePeriod : DateFormatters.getTimePeriod;
  const _getYear = _isFunction(getYear) ? getYear : DateFormatters.getYear;
  const _getMonth = _isFunction(getMonth) ? getMonth : DateFormatters.getMonth;
  const _getShortMonth = _isFunction(getShortMonth) ? getShortMonth : DateFormatters.getShortMonth;
  const _getMonthNumber = _isFunction(getMonthNumber)
    ? getMonthNumber
    : DateFormatters.getMonthNumber;

  return {
    isStartDateBillDate,
    getTimePeriod: _getTimePeriod,
    getYear: _getYear,
    getMonth: _getMonth,
    getShortMonth: _getShortMonth,
    getMonthNumber: _getMonthNumber,
  };
}

const getFormattingConfigMemoized = memoize(getFormattingConfig);

function getDataFormatted(
  data = [],
  minNumberOfBars = 0,
  maxNumberOfBars = 0,
  { isStartDateBillDate, getTimePeriod, getYear, getMonth, getShortMonth, getMonthNumber } = {},
) {
  const { length: dataLength } = data;

  let _data = [];

  if (minNumberOfBars === 0 && maxNumberOfBars === 0) {
    _data = _reverse(_cloneDeep(data));
  } else if (maxNumberOfBars > 0 && (minNumberOfBars === 0 || dataLength > maxNumberOfBars)) {
    const clonedData = _cloneDeep(data);
    const reversedClonedData = _reverse(clonedData);

    _data = _slice(reversedClonedData, 0, maxNumberOfBars);
  } else if (minNumberOfBars !== 0 && dataLength < minNumberOfBars) {
    _data = _reverse(_cloneDeep(data));
    // increase the array size to the min number
    _data[minNumberOfBars - 1] = undefined;
  } else {
    _data = _reverse(_cloneDeep(data));
  }

  const formattedBillsData = _reverse(
    _map(_data, (bar, index) => {
      if (_isUndefined(bar)) {
        const originalFirstBar = data[0];
        const originalFirstBarCurrentIndex = dataLength - 1;

        if (_isUndefined(originalFirstBar)) {
          return {};
        }

        const { startDate: lastBarStartDate, issueDate: lastBarIssueDate } = originalFirstBar;

        const issueDate = DateFormatters.subtractMonthsFromDate(
          lastBarIssueDate,
          index - originalFirstBarCurrentIndex,
        );

        const endDate = DateFormatters.subtractMonthsFromDate(
          lastBarStartDate,
          index - originalFirstBarCurrentIndex - 1,
        );
        const startDate = DateFormatters.subtractMonthsFromDate(endDate, 1);

        const _date = lastBarIssueDate ? issueDate : isStartDateBillDate ? startDate : endDate;

        const additionalInfo = {
          startDate,
          endDate,
          isStartDateBillDate,
          issueDate,
        };

        const timePeriod = getTimePeriod(startDate, endDate, additionalInfo);
        const month = getMonth(_date, additionalInfo);
        const shortMonth = getShortMonth(_date, additionalInfo);
        const monthNumber = getMonthNumber(_date, additionalInfo);
        const year = getYear(_date, additionalInfo);

        return {
          startDate,
          endDate,
          timePeriod,
          month,
          shortMonth,
          monthNumber,
          year,
        };
      }
      const issueDate = bar.issueDate ? moment(bar.issueDate) : null;
      const startDate = moment(bar.startDate);
      const endDate = moment(bar.endDate);

      const _date = issueDate ? issueDate : isStartDateBillDate ? startDate : endDate;

      const additionalInfo = {
        startDate: bar.startDate,
        endDate: bar.endDate,
        isStartDateBillDate,
        issueDate: bar.issueDate,
      };

      const timePeriod = getTimePeriod(startDate, endDate, additionalInfo);
      const month = getMonth(_date, additionalInfo);
      const shortMonth = getShortMonth(_date, additionalInfo);
      const monthNumber = getMonthNumber(_date, additionalInfo);
      const year = getYear(_date, additionalInfo);

      return {
        ...bar,
        timePeriod,
        month,
        shortMonth,
        monthNumber,
        year,
      };
    }),
  );

  let monthsArray = []

  monthsArray = formattedBillsData.map(item => (item?.shortMonth + item?.year))
  monthsArray = monthsArray.length > 0 ? [... new Set(monthsArray)] : []

  const formattedByMonthData = monthsArray?.map(shortMonth => {
    return ({
      shortMonth: formattedBillsData?.filter(el => !!el.id)?.find(el => el?.shortMonth + el?.year === shortMonth)?.shortMonth,
      data: formattedBillsData?.filter(el => !!el.id)?.filter(el => el?.shortMonth + el?.year === shortMonth)
    })
  })

  return formattedByMonthData.filter(el => el.data.length !== 0)
}

const getDataFormattedMemoized = memoize(getDataFormatted);

function BarChart({
  data,
  isLastOutLined,
  onBarPress,
  onActiveIndexChange,
  onBarAnimationEnd,
  minNumberOfBars,
  maxNumberOfBars,
  isStartDateBillDate,
  getTimePeriod,
  getYear,
  getMonth,
  getShortMonth,
  getMonthNumber,
  isLastBarExpanded,
  theme,
  yearStyle,
  verticalLabelsTextStyle,
  lastMonthStyle,
  getCurentActiveIndex,
  showLastMonthTimePeriodOnly,
  getBarStyle,
  barChartDetailsContainerStyle,
  billTypes,
  currentActiveIndex,
  setCurrentActiveIndex,
  ...props
}) {
  const chartViewAnimation = useRef(new Animated.Value(0)).current;
  const billBarAnimation = useRef(new Animated.Value(0)).current;


  const [isYearVisible, setIsYearVisible] = useState(false);

  const previousActiveIndex = usePrevious(currentActiveIndex);

  const formattingConfig = getFormattingConfigMemoized(
    isStartDateBillDate,
    getTimePeriod,
    getYear,
    getMonth,
    getShortMonth,
    getMonthNumber,
  );
  const _data = getDataFormattedMemoized(data, minNumberOfBars, maxNumberOfBars, formattingConfig);
  const hasNegtiveValues = _find(data, bill => bill.value < 0) !== undefined;


  useEffect(() => {
    timingAnimation(chartViewAnimation, 1, 300, 400);
  }, [chartViewAnimation]);

  useEffect(() => {
    if (!_isUndefined(currentActiveIndex) && !_isUndefined(previousActiveIndex)) {
      billBarAnimation.setValue(0);
    }
    const toValue = _isUndefined(currentActiveIndex) ? 0 : 1;
    timingAnimation(billBarAnimation, toValue, ANIMATION_DURATION, undefined, {
      onBarAnimationEnd,
      currentActiveIndex,
      previousActiveIndex,
      data,
      barsData: _data,
    });
  }, [currentActiveIndex, previousActiveIndex]);

  useEffect(() => {
    if (isLastBarExpanded && _data.length > 0) {
      setTimeout(() => {
        onBillBarPress(_data.length - 1);
      }, 0);
    }
  }, []);

  const onBillBarPress = index => {
    setIsYearVisible(false);

    if (!index) {
      setCurrentActiveIndex(undefined);
    }

    if (
      index !== _data.length - 1 &&
      index !== 0 &&
      _data[index].year !== _data[index + 1].year &&
      parseInt(_data[index + 1].monthNumber, 10) === 1
    ) {
      setIsYearVisible(true);
    }

    if (index === currentActiveIndex) {
      // run animation again if click in current again
      setCurrentActiveIndex(undefined);
    } else {
      setCurrentActiveIndex(index);
    }
    _isFunction(onActiveIndexChange) && onActiveIndexChange(index, ANIMATION_DURATION, data, _data);
  };
  // chart display animation
  const transformView = {
    transform: [
      {
        translateX: chartViewAnimation.interpolate({
          inputRange: [0, 1],
          outputRange: [-75, 0],
        }),
      },
    ],
  };

  return (
    <View style={styles.container(theme)} testID="BarChartView">
      <Animated.View style={[styles.barsViewStyle, transformView]}>
        <Bars
          data={_data}
          isLastOutLined={isLastOutLined}
          hasNegtiveValues={hasNegtiveValues}
          onBarDetailsPress={onBarPress}
          onBillBarPress={onBillBarPress}
          currentActiveIndex={currentActiveIndex}
          getBillAnimatedStyle={index =>
            getBillAnimatedStyle(
              billBarAnimation,
              isYearVisible,
              index,
              currentActiveIndex,
              previousActiveIndex,
            )
          }
          yearStyle={yearStyle}
          showLastMonthTimePeriodOnly={showLastMonthTimePeriodOnly}
          getBarStyle={getBarStyle}
          barChartDetailsContainerStyle={barChartDetailsContainerStyle}
          billTypes={billTypes}
        />
      </Animated.View>

      <View style={styles.line(theme)} />

      <Animated.View style={[styles.verticalLabelsViewStyle, transformView]} onStartShouldSetResponder={() => true}>
        <VerticalLabels
          data={_data}
          onBillBarPress={onBillBarPress}
          currentActiveIndex={currentActiveIndex}
          getBillAnimatedStyle={index =>
            getBillAnimatedStyle(
              billBarAnimation,
              isYearVisible,
              index,
              currentActiveIndex,
              previousActiveIndex,
            )
          }
          textStyle={verticalLabelsTextStyle}
          lastMonthStyle={lastMonthStyle}
          getCurentActiveIndex={getCurentActiveIndex}
        />
      </Animated.View>
    </View>
  );
}
BarChart.defaultProps = {
  data: [],
  isLastOutLined: true,
  onBarPress: () => { },
  onActiveIndexChange: () => { },
  minNumberOfBars: 12,
  maxNumberOfBars: 24,
  isStartDateBillDate: false,
  getTimePeriod: DateFormatters.getTimePeriod,
  getYear: DateFormatters.getYear,
  getMonth: DateFormatters.getMonth,
  getShortMonth: DateFormatters.getShortMonth,
  getMonthNumber: DateFormatters.getMonthNumber,
  theme: LightThemeColors,
  showLastMonthTimePeriodOnly: false,
};

BarChart.propTypes = {
  data: PropTypes.array,
  isLastOutLined: PropTypes.bool,
  onBarPress: PropTypes.func,
  onActiveIndexChange: PropTypes.func,
  onBarAnimationEnd: PropTypes.func,
  minNumberOfBars: PropTypes.number,
  maxNumberOfBars: PropTypes.number,
  isStartDateBillDate: PropTypes.bool,
  getTimePeriod: PropTypes.func,
  getYear: PropTypes.func,
  getMonth: PropTypes.func,
  getShortMonth: PropTypes.func,
  getMonthNumber: PropTypes.func,
  isLastBarExpanded: PropTypes.bool,
  theme: PropTypes.object,
  yearStyle: TextPropTypes.style,
  verticalLabelsTextStyle: TextPropTypes.style,
  lastMonthStyle: TextPropTypes.style,
  getCurentActiveIndex: PropTypes.func,
  showLastMonthTimePeriodOnly: PropTypes.bool,
  getBarStyle: PropTypes.func,
  barChartDetailsContainerStyle: ViewPropTypes.style,
  billTypes: PropTypes.object,
};

export default withTheme(BarChart);
export { getFormattingConfig, getDataFormatted, getBillAnimatedStyle };
